import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, downloadFile } from '@/lib/api/client'
import type {
  CatalogueEntry, DatasetView, ReportDefinition, ReportRunResult, RunLogView, SavedReportView,
} from '../types'

const BASE = '/v1/reports'

export function useCatalogue() {
  return useQuery({
    queryKey: ['reports', 'catalogue'],
    queryFn: async () => (await apiClient.get<CatalogueEntry[]>(`${BASE}/catalogue`)).data,
  })
}

export function useDatasets() {
  return useQuery({
    queryKey: ['reports', 'datasets'],
    queryFn: async () => (await apiClient.get<DatasetView[]>(`${BASE}/datasets`)).data,
    // The datasets change when the product changes, not while somebody is building a report.
    staleTime: 60 * 60 * 1000,
  })
}

export function useSavedReports() {
  return useQuery({
    queryKey: ['reports', 'saved'],
    queryFn: async () => (await apiClient.get<SavedReportView[]>(`${BASE}/saved`)).data,
  })
}

export function useRunLog() {
  return useQuery({
    queryKey: ['reports', 'runs'],
    queryFn: async () => (await apiClient.get<RunLogView[]>(`${BASE}/runs`)).data,
  })
}

export function useReportMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['reports'] })

  return {
    run: useMutation({
      mutationFn: async (body: { datasetKey: string; definition: ReportDefinition }) =>
        (await apiClient.post<ReportRunResult>(`${BASE}/run`, body)).data,
      onSuccess: invalidate,
    }),
    runSaved: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<ReportRunResult>(`${BASE}/saved/${id}/run`)).data,
      onSuccess: invalidate,
    }),
    save: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<SavedReportView>(`${BASE}/saved/${id}`, body)).data
          : (await apiClient.post<SavedReportView>(`${BASE}/saved`, body)).data,
      onSuccess: invalidate,
    }),
    download: useMutation({
      mutationFn: async ({ id, name, format }: { id: string; name: string; format: string }) => {
        const extension = format === 'CSV' ? 'csv' : format === 'PDF' ? 'pdf' : 'xlsx'
        await downloadFile(`${BASE}/saved/${id}/export`,
          `${name.replace(/[^A-Za-z0-9-_ ]/g, '').trim()}.${extension}`, { format })
      },
    }),
  }
}
